import React, { createContext, useState, useEffect, useRef } from 'react';
import { AuthState, User } from '../components/layout/Header/Header.types';
import { userAuthApi } from '../features/auth/services';
import { getProgressiveLoginContext } from '../lib/utils/contextAwareAuth';
import { useCaptchaContext, isCaptchaChallenge, type CaptchaChallenge } from '../lib/captcha';
import type { RegistrationConsents } from '../features/legal-consent/types';

/**
 * Auth Context Type
 */
export interface AuthContextType {
  authState: AuthState;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  signup: (userData: SignupData) => Promise<{ success: boolean; errorMessage?: string }>;
  createAnonymousSession: () => Promise<boolean>;
  completeAuthentication: (loginData: {
    user: any;
    tokens: { accessToken: string; refreshToken: string; expiresIn: number; tokenType: string; };
    twoFactorAuth?: any;
  }) => Promise<void>;
}

/**
 * Signup Data Interface
 */
interface SignupData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  /** Legal consent flags for GDPR compliance */
  consents?: RegistrationConsents;
}

/**
 * Auth Context
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user data removed - now using real API

/**
 * Auth Provider Component
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true // Start with loading true during initialization
  });

  // CAPTCHA context for handling risk-based CAPTCHA challenges
  const { showChallenge } = useCaptchaContext();

  // Maximum CAPTCHA retry attempts before failing
  const MAX_CAPTCHA_ATTEMPTS = 3;

  // Token refresh configuration - more conservative approach
  const REFRESH_BUFFER_MS = 2 * 60 * 1000; // 2 minutes (reduced from 5)
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const lastRefreshAttemptRef = useRef<number>(0);
  const MIN_REFRESH_INTERVAL = 60 * 1000; // Minimum 1 minute between refresh attempts

  const scheduleSilentRefresh = (expiryMs: number): void => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const now = Date.now();
    const delay = Math.max(0, expiryMs - REFRESH_BUFFER_MS - now);

    // Don't schedule if token expires too soon or is already expired
    if (delay <= 0 || expiryMs <= now) {
      return;
    }

    
    refreshTimerRef.current = window.setTimeout(() => {
      void silentRefresh();
    }, delay);
  };

  const silentRefresh = async (): Promise<boolean> => {
    const now = Date.now();
    
    // Rate limiting: prevent multiple refresh attempts within MIN_REFRESH_INTERVAL
    if (now - lastRefreshAttemptRef.current < MIN_REFRESH_INTERVAL) {
      return false;
    }

    const refreshToken = localStorage.getItem('amalq_user_refresh_token');
    if (!refreshToken) {
      return false;
    }

    // Single-flight guard - prevent multiple simultaneous refresh attempts
    if (refreshInFlightRef.current) {
      try {
        return await refreshInFlightRef.current;
      } catch {
        return false;
      }
    }

    lastRefreshAttemptRef.current = now;

    refreshInFlightRef.current = (async (): Promise<boolean> => {
      try {
        const refreshResponse = await userAuthApi.refresh(refreshToken);
        if (refreshResponse.success && refreshResponse.data) {
          
          // Update stored tokens
          localStorage.setItem('amalq_user_access_token', refreshResponse.data.accessToken);
          localStorage.setItem('amalq_user_refresh_token', refreshResponse.data.refreshToken);
          const newExpiry = Date.now() + (refreshResponse.data.expiresIn * 1000);
          localStorage.setItem('amalq_user_token_expiry', newExpiry.toString());

          // Schedule next refresh with new expiry time
          scheduleSilentRefresh(newExpiry);
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error('❌ AuthContext: Silent refresh error:', error);
        return false;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    return refreshInFlightRef.current;
  };

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      const savedUser = localStorage.getItem('user');
      const savedAuth = localStorage.getItem('isAuthenticated');
      const accessToken = localStorage.getItem('amalq_user_access_token');
      const refreshToken = localStorage.getItem('amalq_user_refresh_token');
      const tokenExpiry = localStorage.getItem('amalq_user_token_expiry');

      // Check if we have valid tokens and user data
      if (savedAuth === 'true' && savedUser && accessToken && refreshToken) {
        try {
          const user = JSON.parse(savedUser);

          // Check if access token is still valid
          if (tokenExpiry && Date.now() < parseInt(tokenExpiry)) {
            setAuthState({
              isAuthenticated: true,
              user,
              isLoading: false
            });

            // Schedule proactive silent refresh
            scheduleSilentRefresh(parseInt(tokenExpiry));
          } else {
            // Token expired, try to refresh
            // Access token expired, attempting refresh
            try {
              const ok = await silentRefresh();

              if (ok) {
                // Token refreshed, restoring session
                setAuthState({
                  isAuthenticated: true,
                  user,
                  isLoading: false
                });
              } else {
                throw new Error('Token refresh failed');
              }
            } catch (refreshError) {
              console.error('❌ AuthContext: Token refresh failed:', refreshError);
              // Clear invalid tokens and user data
              localStorage.removeItem('user');
              localStorage.removeItem('isAuthenticated');
              localStorage.removeItem('amalq_user_access_token');
              localStorage.removeItem('amalq_user_refresh_token');
              localStorage.removeItem('amalq_user_token_expiry');

              setAuthState({
                isAuthenticated: false,
                user: null,
                isLoading: false
              });
            }
          }
        } catch (error) {
          console.error('❌ AuthContext: Failed to parse saved user data:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('amalq_user_access_token');
          localStorage.removeItem('amalq_user_refresh_token');
          localStorage.removeItem('amalq_user_token_expiry');
        }
      } else {
        // No saved authentication data, set loading to false
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false
        });
      }
    };

    initializeAuth();

    // On visibility change (tab focus), check if refresh is needed but be conservative
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        const tokenExpiry = localStorage.getItem('amalq_user_token_expiry');
        const refreshToken = localStorage.getItem('amalq_user_refresh_token');
        
        if (tokenExpiry && refreshToken) {
          const timeLeft = parseInt(tokenExpiry) - Date.now();
          const now = Date.now();
          
          // Only refresh if:
          // 1. Token expires within buffer time
          // 2. No recent refresh attempt
          // 3. Not already refreshing
          if (timeLeft <= REFRESH_BUFFER_MS && 
              now - lastRefreshAttemptRef.current >= MIN_REFRESH_INTERVAL &&
              !refreshInFlightRef.current) {
            void silentRefresh();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return (): void => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  // Cross-tab auth synchronization using BroadcastChannel + storage event fallback
  // Handles: login in Tab B → update Tab A, logout in Tab B → update Tab A
  const authChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    // Create BroadcastChannel for explicit cross-tab messaging (more reliable than storage events)
    // Wrapped in try-catch: BroadcastChannel is not available on Safari < 15.4 / some iOS WebKit
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('amalq_auth_sync');
      authChannelRef.current = channel;
    } catch {
      // BroadcastChannel not supported -- fall through to storage event listener below
    }

    // Handle messages from other tabs (only if BroadcastChannel was created)
    if (channel) channel.onmessage = (event: MessageEvent<{ type: 'LOGIN' | 'LOGOUT' | 'USER_UPDATE'; payload?: unknown }>): void => {
      const { type, payload } = event.data;

      if (type === 'LOGOUT') {
        console.log('🔐 AuthContext: Cross-tab LOGOUT received');
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false
        });
        // Clear refresh timer
        if (refreshTimerRef.current) {
          window.clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      }

      if (type === 'LOGIN' && payload) {
        console.log('🔐 AuthContext: Cross-tab LOGIN received');
        const { user, tokenExpiry } = payload as { user: User; tokenExpiry: number };
        setAuthState({
          isAuthenticated: true,
          user,
          isLoading: false
        });
        // Schedule refresh for this tab
        if (tokenExpiry) {
          scheduleSilentRefresh(tokenExpiry);
        }
      }

      if (type === 'USER_UPDATE' && payload) {
        console.log('🔐 AuthContext: Cross-tab USER_UPDATE received');
        setAuthState(prev => ({
          ...prev,
          user: payload as User
        }));
      }
    };

    // Fallback: Also listen for storage events (for browsers without BroadcastChannel support)
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === 'amalq_user_access_token') {
        if (!event.newValue) {
          // Token was removed in another tab → logout
          console.log('🔐 AuthContext: Cross-tab logout (storage event fallback)');
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false
          });
          if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
          }
        } else if (event.newValue && event.oldValue === null) {
          // Token was added (was null, now has value) → login
          console.log('🔐 AuthContext: Cross-tab login (storage event fallback)');
          const savedUser = localStorage.getItem('user');
          const tokenExpiry = localStorage.getItem('amalq_user_token_expiry');

          if (savedUser) {
            try {
              const user = JSON.parse(savedUser);
              setAuthState({
                isAuthenticated: true,
                user,
                isLoading: false
              });
              if (tokenExpiry) {
                scheduleSilentRefresh(parseInt(tokenExpiry));
              }
            } catch (error) {
              console.error('❌ AuthContext: Failed to parse user in cross-tab sync:', error);
            }
          }
        }
      }

      // Handle user data changes
      if (event.key === 'user' && event.newValue) {
        try {
          const updatedUser = JSON.parse(event.newValue);
          setAuthState(prev => {
            // Only update if we're authenticated
            if (prev.isAuthenticated) {
              console.log('🔐 AuthContext: Cross-tab user data updated (storage event)');
              return { ...prev, user: updatedUser };
            }
            return prev;
          });
        } catch (error) {
          console.error('❌ AuthContext: Failed to parse updated user data:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return (): void => {
      if (channel) channel.close();
      authChannelRef.current = null;
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // No dependencies - stable listener

  // Helper to broadcast auth changes to other tabs
  const broadcastAuthChange = (type: 'LOGIN' | 'LOGOUT' | 'USER_UPDATE', payload?: unknown): void => {
    if (authChannelRef.current) {
      authChannelRef.current.postMessage({ type, payload });
    }
  };

  /**
   * Complete Authentication Helper
   * Handles the final steps of successful authentication
   */
  const completeAuthentication = async (loginData: {
    user: any;
    tokens: { accessToken: string; refreshToken: string; expiresIn: number; tokenType: string; };
    twoFactorAuth?: any;
  }): Promise<void> => {
    // Extract user data from the API response
    const user: User = {
      id: loginData.user.id,
      name: loginData.user.name,
      email: loginData.user.email,
      avatar: loginData.user.name ? `https://ui-avatars.com/api/?name=${encodeURIComponent(loginData.user.name)}&background=10b981&color=fff` : undefined,
      role: 'user' // Regular users have 'user' role
    };

    // Store tokens securely
    localStorage.setItem('amalq_user_access_token', loginData.tokens.accessToken);
    localStorage.setItem('amalq_user_refresh_token', loginData.tokens.refreshToken);
    const tokenExpiry = Date.now() + (loginData.tokens.expiresIn * 1000);
    localStorage.setItem('amalq_user_token_expiry', tokenExpiry.toString());

    setAuthState({
      isAuthenticated: true,
      user,
      isLoading: false
    });

    // Save user data for persistence
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('isAuthenticated', 'true');

    // Schedule token refresh
    scheduleSilentRefresh(tokenExpiry);

    // Broadcast login to other tabs
    broadcastAuthChange('LOGIN', { user, tokenExpiry });

    console.log('🔐 AuthContext: Authentication completed successfully');
  };

  /**
   * Login Function - Enhanced with Smart 2FA Flow, Device Fingerprinting, and Risk-Based CAPTCHA
   * Now handles device trust, progressive security, incognito detection, and CAPTCHA challenges
   */
  const login = async (email: string, password: string): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    let hcaptchaToken: string | undefined;
    let attempts = 0;

    try {
      // CAPTCHA retry loop - handles risk-based CAPTCHA challenges
      while (attempts < MAX_CAPTCHA_ATTEMPTS) {
        // Login includes automatic device fingerprinting and incognito detection
        console.log('🔐 AuthContext: Starting login with enhanced device fingerprinting',
          hcaptchaToken ? '(with CAPTCHA token)' : '');

        const response = await userAuthApi.login({ email, password, hcaptchaToken });

        // Check if CAPTCHA challenge is required (risk assessment triggered)
        if (isCaptchaChallenge(response)) {
          console.log('🔐 AuthContext: CAPTCHA challenge required', {
            mode: response.captchaMode,
            attempt: attempts + 1
          });

          try {
            // Show CAPTCHA challenge (invisible or visible based on risk score)
            hcaptchaToken = await showChallenge(response as CaptchaChallenge);
            attempts++;
            continue; // Retry with token
          } catch (captchaError) {
            // User closed CAPTCHA or it failed
            console.warn('🔐 AuthContext: CAPTCHA challenge failed or cancelled', captchaError);
            setAuthState(prev => ({ ...prev, isLoading: false }));
            return false;
          }
        }

        // Normal response handling
        if (response.success) {
          // Get device context for smart authentication decisions
          const loginContext = getProgressiveLoginContext();

          console.log('🔐 AuthContext: Device context analysis:', {
            deviceType: loginContext.context.type,
            location: loginContext.context.location,
            timeOfDay: loginContext.context.timeOfDay,
            riskLevel: loginContext.context.riskLevel,
            trustScore: loginContext.context.trustScore,
            shouldSkipTwoFactor: loginContext.shouldSkipTwoFactor
          });

          // Check 2FA requirements with context-aware logic
          const { twoFactorAuth } = response.data;

          console.log('🔐 AuthContext: Login response - 2FA status:', {
            twoFactorAuth: twoFactorAuth ? {
              enabled: twoFactorAuth.enabled,
              required: twoFactorAuth.required,
              deviceTrusted: twoFactorAuth.deviceTrusted,
              method: twoFactorAuth.method
            } : 'Not present (2FA disabled)'
          });

          // Backend 2FA decision: Backend is ALWAYS the source of truth for security
          // Device context is advisory only - never override backend security requirements
          const requiresVerification = twoFactorAuth?.required === true;

          if (requiresVerification) {
            console.log('🔐 AuthContext: 2FA verification required (context-aware decision)');

            // Clear any existing pending login data to prevent stale sessionId issues
            sessionStorage.removeItem('amalq_pending_login');

            // Debug: Log the authentication data structure
            console.log('🔐 AuthContext: Response data structure:', response.data);
            const responseData = response.data as typeof response.data & { tokens?: unknown; sessionId?: string };
            console.log('🔐 AuthContext: Has tokens:', !!responseData.tokens);
            console.log('🔐 AuthContext: Has sessionId:', !!responseData.sessionId);

            // Store login data with context information
            // When 2FA is required, API returns sessionId instead of tokens
            const pendingLoginData = {
              user: response.data.user,
              tokens: responseData.tokens || { sessionId: responseData.sessionId },
              twoFactorAuth: response.data.twoFactorAuth,
              deviceContext: loginContext.context,
              authPolicy: loginContext.policy
            };

            console.log('🔐 AuthContext: Storing pending login data:', pendingLoginData);
            sessionStorage.setItem('amalq_pending_login', JSON.stringify(pendingLoginData));

            setAuthState(prev => ({ ...prev, isLoading: false }));

            // Navigate to 2FA verification page
            return false; // Don't complete login yet
          }

          // Direct login success (trusted device/context or 2FA not enabled)
          console.log('🔐 AuthContext: Context-aware direct login success');
          await completeAuthentication(response.data);
          return true;

        } else {
          // Login failed (not a CAPTCHA challenge, actual auth failure)
          setAuthState(prev => ({ ...prev, isLoading: false }));
          return false;
        }
      }

      // Max CAPTCHA attempts reached
      console.warn('🔐 AuthContext: Max CAPTCHA attempts reached');
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return false;

    } catch (error) {
      console.error('❌ AuthContext: Login error:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  };

  /**
   * Logout Function - Real API Implementation
   */
  const logout = async (): Promise<void> => {

    try {
      // Get the access token for API call
      const accessToken = localStorage.getItem('amalq_user_access_token');

      // Call logout API if we have a token. TanStack Query cache is invalidated
      // separately by AuthOverlay/route guards on auth state change.
      if (accessToken) {
        await userAuthApi.logout(accessToken);
      }
    } catch (error) {
      console.error('❌ AuthContext: Logout API error:', error);
      // Continue with local cleanup even if API call fails
    }

    // Clear authentication state
    setAuthState({
      isAuthenticated: false,
      user: null,
      isLoading: false
    });

    // Clear all stored data
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('amalq_user_access_token');
    localStorage.removeItem('amalq_user_refresh_token');
    localStorage.removeItem('amalq_user_token_expiry');

    // Broadcast logout to other tabs
    broadcastAuthChange('LOGOUT');

    console.log('🔐 AuthContext: Logout completed, broadcast sent to other tabs');
  };

  /**
   * Signup Function - Real API Implementation with Device Fingerprinting and Risk-Based CAPTCHA
   */
  const signup = async (userData: SignupData): Promise<{ success: boolean; errorMessage?: string }> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    // Client-side validation
    if (userData.password !== userData.confirmPassword) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return { success: false, errorMessage: 'Passwords do not match' };
    }

    let hcaptchaToken: string | undefined;
    let attempts = 0;

    try {
      // CAPTCHA retry loop - handles risk-based CAPTCHA challenges
      while (attempts < MAX_CAPTCHA_ATTEMPTS) {
        // Registration includes automatic device fingerprinting for security
        console.log('🔐 AuthContext: Starting registration with device fingerprinting',
          hcaptchaToken ? '(with CAPTCHA token)' : '');

        const response = await userAuthApi.register({
          email: userData.email,
          password: userData.password,
          name: userData.name,
          isAnonymous: false,
          hcaptchaToken,
          consents: userData.consents
        });

        // Check if CAPTCHA challenge is required (risk assessment triggered)
        if (isCaptchaChallenge(response)) {
          console.log('🔐 AuthContext: CAPTCHA challenge required for registration', {
            mode: response.captchaMode,
            attempt: attempts + 1
          });

          try {
            // Show CAPTCHA challenge (invisible or visible based on risk score)
            hcaptchaToken = await showChallenge(response as CaptchaChallenge);
            attempts++;
            continue; // Retry with token
          } catch (captchaError) {
            // User closed CAPTCHA or it failed
            console.warn('🔐 AuthContext: CAPTCHA challenge failed or cancelled', captchaError);
            setAuthState(prev => ({ ...prev, isLoading: false }));
            return { success: false, errorMessage: 'Security verification cancelled. Please try again.' };
          }
        }

        // Normal response handling
        if (response.success) {
          // Extract user data from the API response
          const user: User = {
            id: response.data.user.id,
            name: response.data.user.name,
            email: response.data.user.email,
            avatar: response.data.user.name ? `https://ui-avatars.com/api/?name=${encodeURIComponent(response.data.user.name)}&background=10b981&color=fff` : undefined,
            role: 'user' // Regular users have 'user' role
          };

          // Store tokens securely
          localStorage.setItem('amalq_user_access_token', response.data.tokens.accessToken);
          localStorage.setItem('amalq_user_refresh_token', response.data.tokens.refreshToken);
          const tokenExpiry = Date.now() + (response.data.tokens.expiresIn * 1000);
          localStorage.setItem('amalq_user_token_expiry', tokenExpiry.toString());

          setAuthState({
            isAuthenticated: true,
            user,
            isLoading: false
          });

          // Save user data for persistence
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('isAuthenticated', 'true');

          // Schedule token refresh
          scheduleSilentRefresh(tokenExpiry);

          // Broadcast signup login to other tabs
          broadcastAuthChange('LOGIN', { user, tokenExpiry });

          return { success: true };
        } else {
          // Registration failed (not a CAPTCHA challenge, actual failure)
          setAuthState(prev => ({ ...prev, isLoading: false }));
          return { success: false, errorMessage: response.message || 'Registration failed' };
        }
      }

      // Max CAPTCHA attempts reached
      console.warn('🔐 AuthContext: Max CAPTCHA attempts reached during registration');
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return { success: false, errorMessage: 'Security verification failed. Please try again later.' };

    } catch (error) {
      console.error('❌ AuthContext: Registration error:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));

      // Extract error message from API response if available
      let errorMessage = 'Unable to create your account. Please try again.';
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }

      return { success: false, errorMessage };
    }
  };

  /**
   * Create Anonymous Session - Backend Guide Compliance with Device Fingerprinting
   */
  const createAnonymousSession = async (): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    try {
      // Anonymous session creation includes device fingerprinting for tracking
      console.log('🔐 AuthContext: Creating anonymous session with device fingerprinting');
      const response = await userAuthApi.createAnonymous();

      if (response.success) {
        // Extract anonymous user data
        const user: User = {
          id: response.data.user.id,
          name: response.data.user.name || 'Anonymous Donor',
          email: response.data.user.email || '',
          avatar: undefined, // Anonymous users don't have avatars
          role: 'user'
        };

        // Store tokens securely
        localStorage.setItem('amalq_user_access_token', response.data.tokens.accessToken);
        localStorage.setItem('amalq_user_refresh_token', response.data.tokens.refreshToken);
        const tokenExpiry = Date.now() + (response.data.tokens.expiresIn * 1000);
        localStorage.setItem('amalq_user_token_expiry', tokenExpiry.toString());

        setAuthState({
          isAuthenticated: true,
          user,
          isLoading: false
        });

        // Save user data for persistence
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('isAuthenticated', 'true');

        // Schedule token refresh
        scheduleSilentRefresh(tokenExpiry);

        // Broadcast anonymous session to other tabs
        broadcastAuthChange('LOGIN', { user, tokenExpiry });

        return true;
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return false;
      }
    } catch (error) {
      console.error('❌ AuthContext: Anonymous session error:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  };

  const value: AuthContextType = {
    authState,
    login,
    logout,
    signup,
    createAnonymousSession,
    completeAuthentication
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// useAuth hook moved to src/hooks/useAuth.ts for Fast Refresh compatibility
